import { supabase } from "@/integrations/supabase/client";
import { API_CONFIG } from "@/config/api";
import { ensureDemoStudentForClass } from "@/services/demoStudentService";
import {
  TEACHER_SESSION_BACKUP_KEY,
  TeacherSessionBackup,
  buildTeacherSessionBackup,
  clearTutorLocalCache,
  parseTeacherSessionBackup,
} from "@/utils/impersonationUtils";

type ImpersonateDemoResponse = {
  session: { access_token: string; refresh_token: string };
  demoStudentId: string;
  classId: string;
};

export class ImpersonationError extends Error {
  /** When true the teacher session could not be restored — re-login is needed. */
  readonly needsLogin: boolean;
  constructor(message: string, needsLogin = false) {
    super(message);
    this.name = "ImpersonationError";
    this.needsLogin = needsLogin;
  }
}

function readBackup(): TeacherSessionBackup | null {
  try {
    return parseTeacherSessionBackup(sessionStorage.getItem(TEACHER_SESSION_BACKUP_KEY));
  } catch {
    return null;
  }
}

function writeBackup(backup: TeacherSessionBackup): void {
  sessionStorage.setItem(TEACHER_SESSION_BACKUP_KEY, JSON.stringify(backup));
}

/** True while a teacher session is parked (i.e. we are impersonating a demo). */
export function isImpersonationBackupPresent(): boolean {
  return readBackup() !== null;
}

/** Forgets the parked teacher session — called on logout / successful switch-back. */
export function clearTeacherSessionBackup(): void {
  try {
    sessionStorage.removeItem(TEACHER_SESSION_BACKUP_KEY);
  } catch (error) {
    console.error("[impersonation] clearTeacherSessionBackup failed:", error);
  }
}

/**
 * Drops a stale backup once the user is provably back to a non-demo identity
 * (fresh login after expiry, another device, …). The 60s grace period exists
 * so the transient "teacher profile still in state, demo session just set"
 * window of a forward switch can never wipe the backup we just wrote.
 */
export function reconcileImpersonationBackup(
  currentUserId: string | null,
  isTestStudent: boolean
): void {
  const backup = readBackup();
  if (!backup || !currentUserId) return;
  if (isTestStudent) return;
  if (currentUserId !== backup.teacherId) return;
  if (Date.now() - backup.createdAt < 60_000) return;
  clearTeacherSessionBackup();
}

async function callImpersonateDemo(
  classId: string,
  accessToken: string
): Promise<ImpersonateDemoResponse> {
  const res = await fetch(API_CONFIG.IMPERSONATE_DEMO_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ classId }),
  });

  if (res.status === 404) {
    const body = await res.json().catch(() => ({}));
    if (body?.code === "DEMO_NOT_FOUND") {
      throw new ImpersonationError("DEMO_NOT_FOUND");
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ImpersonationError(
      typeof body?.error === "string" ? body.error : `Demo switch failed (${res.status})`
    );
  }

  const json = await res.json();
  if (!json?.session?.access_token || !json?.session?.refresh_token || !json?.demoStudentId) {
    throw new ImpersonationError("Demo session is incomplete");
  }
  return json as ImpersonateDemoResponse;
}

/**
 * Teacher → demo student. Seamless: no credentials are asked for.
 *
 *  1. Ensures the class demo account exists (idempotent, existing service).
 *  2. Parks the teacher's current session in sessionStorage.
 *  3. Asks the impersonate-demo edge function for a demo session
 *     (authorised server-side; demo password never touches the browser).
 *  4. Installs the demo session via setSession — AuthProvider picks the new
 *     user id up through onAuthStateChange and loads the student profile,
 *     so the teacher instantly has the student role, tutor access, etc.
 */
export async function switchToDemoAccount(classId: string): Promise<{ demoStudentId: string }> {
  const {
    data: { session: teacherSession },
  } = await supabase.auth.getSession();

  if (!teacherSession?.access_token || !teacherSession?.refresh_token || !teacherSession?.user) {
    throw new ImpersonationError("You must be logged in as a teacher");
  }
  const teacherId = teacherSession.user.id;

  // Idempotent: creates + links the demo student on first use.
  const { studentId: ensuredDemoId } = await ensureDemoStudentForClass(classId);

  let impersonated: ImpersonateDemoResponse;
  try {
    impersonated = await callImpersonateDemo(classId, teacherSession.access_token);
  } catch (error) {
    // Genuine 404 (demo row invisible to the edge lookup): ensure once more
    // and retry a single time before giving up.
    if (error instanceof ImpersonationError && error.message === "DEMO_NOT_FOUND") {
      await ensureDemoStudentForClass(classId);
      try {
        impersonated = await callImpersonateDemo(classId, teacherSession.access_token);
      } catch {
        throw new ImpersonationError("Demo účet se nepodařilo připravit. Zkuste to prosím znovu.");
      }
    } else {
      throw error;
    }
  }

  // Park the teacher session BEFORE switching, so switch-back never needs
  // the teacher's password. sessionStorage (not localStorage): tab-scoped,
  // cleared on explicit logout, invisible to other tabs.
  writeBackup(
    buildTeacherSessionBackup({
      access_token: teacherSession.access_token,
      refresh_token: teacherSession.refresh_token,
      teacherId,
      classId,
      demoStudentId: impersonated.demoStudentId ?? ensuredDemoId,
    })
  );

  try {
    clearTutorLocalCache();
    const { error } = await supabase.auth.setSession({
      access_token: impersonated.session.access_token,
      refresh_token: impersonated.session.refresh_token,
    });
    if (error) throw error;
  } catch (error) {
    // Don't leave a stale backup behind if the switch itself failed.
    clearTeacherSessionBackup();
    console.error("[impersonation] setSession(demo) failed:", error);
    throw new ImpersonationError("Přepnutí na demo účet se nezdařilo. Zkuste to prosím znovu.");
  }

  return { demoStudentId: impersonated.demoStudentId };
}

/**
 * Demo → teacher. Purely local: reinstalls the parked teacher session.
 * Returns the classId so the UI can navigate back to the class detail.
 */
export async function switchBackToTeacher(): Promise<{ classId: string }> {
  const backup = readBackup();
  if (!backup) {
    throw new ImpersonationError("No teacher session to return to. Please log in again.", true);
  }

  clearTutorLocalCache();
  const { data, error } = await supabase.auth.setSession({
    access_token: backup.access_token,
    refresh_token: backup.refresh_token,
  });

  if (error) {
    console.error("[impersonation] setSession(teacher) failed:", error);
    clearTeacherSessionBackup();
    throw new ImpersonationError(
      "Učitelskou relaci se nepodařilo obnovit. Přihlaste se prosím znovu.",
      true
    );
  }

  // Defense in depth: a stale intermediary (service worker, proxy, …) could
  // answer the session's user lookup with another identity's cached response.
  // Never accept a restored session whose user is not the parked teacher —
  // a silent wrong-identity session is worse than asking to log in again.
  if (data?.user?.id !== backup.teacherId) {
    console.error("[impersonation] restored session user mismatch:", {
      expected: backup.teacherId,
      got: data?.user?.id ?? null,
    });
    await supabase.auth.signOut().catch(() => undefined);
    clearTeacherSessionBackup();
    throw new ImpersonationError(
      "Učitelskou relaci se nepodařilo ověřit. Přihlaste se prosím znovu.",
      true
    );
  }

  clearTeacherSessionBackup();
  return { classId: backup.classId };
}

/** classId of the class being demoed, if any (for headers / diagnostics). */
export function getImpersonatedClassId(): string | null {
  return readBackup()?.classId ?? null;
}
